import React, { useState, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import FocusTrap from 'focus-trap-react';
import LocationFormatter from '../_cart/location-formatter';
import genericHelpers from '../_utils/generic-helpers';
import WarningMessage from '../_utils/warning-message';
import ConfirmStore from './confirm-store';
import { useStorePickerContext } from './store-picker-context';

const StoreInformation = ({ handleStorePicker }) => {
  const {
    locations,
    alternatePickupStore: alternateStore,
    handleOnSave: setStoreToAlternate,
    zipCode: zipCodeProp,
  } = useStorePickerContext();
  const storeSource = locations.storeSource;
  const [showConfirmation, setShowConfirmation] = useState(false);

  const isLocationSelected = useCallback(() => {
    return locations.available.some(
      (store) => String(store.id) === String(locations.selected),
    );
  }, [locations.available, locations.selected]);

  const storeAvailable = isLocationSelected();

  const selectedStore = useMemo(
    () => locations.pickup.find((pickup) => pickup.id === locations.selected),
    [locations.pickup, locations.selected],
  );

  const {
    name: selectedStoreName,
    storeType: selectedStoreType,
    pickupTimesFormatted,
  } = new LocationFormatter(selectedStore);

  const alternateAvailableStore = useMemo(() => {
    return locations.available[0]
      ? new LocationFormatter(locations.available[0])
      : { name: '', storeType: '' };
  }, [locations.available]);

  const handleStoreChange = useCallback(
    (e) => {
      e.preventDefault();
      if (storeSource === 'cart') {
        setShowConfirmation(true);
      } else {
        setStoreToAlternate(alternateStore.id);
      }
    },
    [setStoreToAlternate, alternateStore, storeSource],
  );

  const cancelChange = () => setShowConfirmation(false);

  const saveStore = () => {
    setStoreToAlternate(alternateStore.id);
    setShowConfirmation(false);
  };

  const modalPortal = document.getElementById('react-store-picker');

  const confirmModal = showConfirmation && modalPortal
    ? ReactDOM.createPortal(
        <FocusTrap>
          <div
            className="modal d-flex align-items-center justify-content-center store-confirm-modal"
            tabIndex="-1"
            role="dialog">
            <div className="modal-dialog" role="document">
              <ConfirmStore
                currentStore={selectedStore}
                newStore={locations.available[0]}
                cancelChange={cancelChange}
                saveStore={saveStore}
              />
            </div>
          </div>
        </FocusTrap>,
        modalPortal,
      )
    : null;

  function Prompts() {
    // No stores location retrieved
    if (!locations.pickup || locations.pickup.length === 0) {
      return (
        <p>
          <span className="store-name">This product is Out of Stock.</span>
          <button className="btn-change" onClick={handleStorePicker}>
            Change Store
          </button>
        </p>
      );
    }

    if (storeAvailable) {
      // store is available
      return (
        <>
          <p className="store-name">
            {genericHelpers.capitalizeWordsInString(
              `${selectedStoreName}, ${selectedStoreType}`,
            )}
            <button
              className="btn-change"
              onClick={() => handleStorePicker(true)}>
              Change Store
            </button>
          </p>
          {pickupTimesFormatted.length > 0 && (
            <p className="pickup-time">
              Pickup as soon as&nbsp;
              <span className="store-name ml-2">
                {pickupTimesFormatted[0].replace(/(PM|AM)/i, (match) =>
                  match.toLowerCase(),
                )}
              </span>
            </p>
          )}
          <p>
            <a
              href={`/store-finder?address=${zipCodeProp}`}
              className="btn-link"
              target="_blank"
              rel="noreferrer">
              <i className="fas fa-map-marker-alt mr-2 "></i>
              Photo Center
            </a>
          </p>
        </>
      );
    } else if (!storeAvailable && locations.available?.length > 0) {
      const storeName = genericHelpers.capitalizeWordsInString(
        `${alternateAvailableStore.name},  ${alternateAvailableStore.storeType}`,
      );
      const storeDistanceMiles = locations.available[0]?.distance?.miles ?? '';
      // return option to change store
      return (
        <>
          <p>
            <span className="store-name">Out of Stock</span>
            {selectedStoreName && selectedStoreType && (
              <>
                at&nbsp;
                <span className="unavailable-store">
                  {genericHelpers.capitalizeWordsInString(
                    `${selectedStoreName}, ${selectedStoreType}`,
                  )}
                </span>
              </>
            )}
            <button className="btn-change" onClick={handleStorePicker}>
              Change Store
            </button>
          </p>
          <WarningMessage
            className="bleed-warning store-unavailable-warning"
            dataTestId="store-unavailable-error"
            hidePrefix>
            <span>
              Your closest store with availability is {storeName},&nbsp;
              {storeDistanceMiles} miles away.&nbsp;
              <a
                role="button"
                href="#"
                key="change"
                data-test-id="change-store"
                onClick={handleStoreChange}>Click to change to that location.</a>
            </span>
          </WarningMessage>
        </>
      );
    } else {
      // not available
      return (
        <p>
          <span className="store-name">This product is Out of Stock.</span>
          <button className="btn-change" onClick={handleStorePicker}>
            Change Store
          </button>
        </p>
      );
    }
  }

  return (
    <>
      {confirmModal}
      <Prompts />
    </>
  );
};

StoreInformation.propTypes = {
  handleStorePicker: PropTypes.func.isRequired,
};

export default StoreInformation;
